import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import FormConsulate, { ConsulateForm } from 'system/geographic/components/consulate/form';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonConsulateCrumbs } from '../constants';
import { HttpResponse } from 'shell/models/http-response';
import { Consulate } from 'shell/models/consulate';
import { toast } from 'react-toastify';
import TerritoryAutoCompleteTable from 'system/geographic/components/consulate/form/territory';

const ConsulateEditPage = (): React.ReactElement => {
  const params = useParams();

  const { put: updateConsulate } = useHttpFetch(
    `${__MUNICIPALITY_API_URL__}/geo/consolato/${params.id}`
  );

  const {
    get: getConsolate,
    data,
    error,
    loading,
  } = useHttpFetch<HttpResponse<Consulate>>(
    `${__MUNICIPALITY_API_URL__}/geo/consolato/${params.id}`
  );

  useEffect(() => {
    getConsolate();
  }, []);

  const consulate: ConsulateForm = useMemo(() => {
    const consulatePayload = data?.payload;
    if (!consulatePayload || !!error) {
      return {};
    }
    return {
      code: consulatePayload.key.codice,
      name: consulatePayload.nome,
      address: consulatePayload.indirizzo,
      category: consulatePayload.categoria,
      municipality:
        consulatePayload.comune?.nome || consulatePayload.comune?.key?.codice
          ? {
              description: consulatePayload.comune?.nome || '',
              code: consulatePayload.comune?.key?.codice || '',
            }
          : undefined,
      territory:
        consulatePayload.territori?.map((territory) => ({
          code: territory.key.codice,
          description: territory.esonimo,
        })) || [],
    };
  }, [data, error]);

  const navigate = useNavigate();

  const onSubmit = useCallback(
    (formValues: ConsulateForm | undefined) => {
      updateConsulate({
        nome: formValues?.name,
        categoria: formValues?.category || '',
        indirizzo: formValues?.address || '',
        territori:
          formValues?.territory?.map((territory) => ({
            key: {
              codice: territory.code,
            },
          })) || [],
        sede: Number.parseInt(formValues?.municipality?.code || '', 10),
        key: {
          codice: formValues?.code,
        },
      }).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/sistema/geografiche/estero/consolato');
      });
    },
    [updateConsulate, navigate]
  );

  return (
    <Fragment>
      <Breadcrumbs items={[...CommonConsulateCrumbs, { name: 'Modifica Consolato' }]} />
      {loading || error || !data ? undefined : (
        <FormConsulate
          title="Modifica Consolato"
          onOk={onSubmit}
          initialValues={consulate}
          isEdit
          onCancel={() => {
            navigate('/sistema/geografiche/estero/consolato');
          }}
        >
          {(formConsulate) => (
            <TerritoryAutoCompleteTable
              selectedValues={formConsulate?.values.territory || []}
              handleAdd={(element) => {
                const newTerritories = formConsulate?.values.territory || [];
                const foundElement = newTerritories.find(
                  (territory) => territory.code === element?.code
                );
                if (!foundElement) {
                  formConsulate?.setFieldValue('territory', [...newTerritories, element]);
                }
              }}
              handleRemove={(element) => {
                const oldTerritories = formConsulate?.values.territory || [];
                const newTerritories = oldTerritories.filter((oldTerritory) => {
                  return oldTerritory.code !== element?.code;
                });
                formConsulate?.setFieldValue('territory', newTerritories);
              }}
              error={formConsulate?.touched.territory && Boolean(formConsulate?.errors.territory)}
            />
          )}
        </FormConsulate>
      )}
    </Fragment>
  );
};

ConsulateEditPage.displayName = 'Consulate edit page component';

export default ConsulateEditPage;
